import { normalizarHabilidadeId } from '../../lib/skillAliases.js';

const DAY_MS = 86400000;

function asList(value) {
  if (!value) return [];
  return Array.isArray(value) ? value.filter(Boolean) : [value];
}

function numeric(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function dateOf(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function skillIds(meta = {}) {
  return asList(meta.habilidades || meta.habilidades_alvo || meta.habilidade_alvo || meta.habilidade)
    .map(normalizarHabilidadeId)
    .filter(Boolean);
}

function logSkills(log = {}) {
  return asList(log.habilidades || log.habilidades_alvo || log.habilidade)
    .concat((log.itens || []).flatMap((item) => item.habilidades || item.habilidadesAlvo || []))
    .map(normalizarHabilidadeId)
    .filter(Boolean);
}

function scoresForMeta(meta, logs = []) {
  const targets = new Set(skillIds(meta));
  return logs.flatMap((log) => {
    const date = log.data || log.date || log.createdAt;
    return (log.itens || []).flatMap((item) => {
      const itemSkills = asList(item.habilidades || item.habilidadesAlvo).map(normalizarHabilidadeId);
      const related = !targets.size || itemSkills.some((skill) => targets.has(skill));
      const score = numeric(item.score);
      return related && score !== null ? [{ score: score <= 1 ? score * 100 : score, date }] : [];
    });
  }).sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
}

export function analyzePeiGoal(meta, logs = [], today = new Date()) {
  const relatedLogs = logs.filter((log) => {
    const targets = new Set(skillIds(meta));
    if (!targets.size) return true;
    return logSkills(log).some((skill) => targets.has(skill));
  });
  const scores = scoresForMeta(meta, relatedLogs);
  const recent = scores.slice(-3);
  const measured = recent.length ? Math.round(recent.reduce((sum, item) => sum + item.score, 0) / recent.length) : null;
  const baseline = numeric(meta.linha_base) ?? 0;
  const target = numeric(meta.alvo_numerico) ?? 100;
  const current = measured ?? baseline;
  const denominator = Math.max(1, target - baseline);
  const progress = current >= target ? 100 : Math.max(0, Math.min(100, Math.round(((current - baseline) / denominator) * 100)));
  const deadline = dateOf(meta.prazo);
  const daysToDeadline = deadline ? Math.ceil((deadline.getTime() - today.getTime()) / DAY_MS) : null;
  const lastDate = relatedLogs.map((log) => dateOf(log.data || log.date || log.createdAt)).filter(Boolean).sort((a, b) => b - a)[0] || null;
  const daysWithoutService = lastDate ? Math.floor((today.getTime() - lastDate.getTime()) / DAY_MS) : null;
  const overdue = daysToDeadline !== null && daysToDeadline < 0 && progress < 100;
  const withoutService = relatedLogs.length === 0 || (daysWithoutService !== null && daysWithoutService > 30);
  const completed = progress >= 100 || meta.status === 'concluida' || meta.status === 'concluído';
  const status = completed ? 'concluida' : overdue ? 'vencida' : withoutService ? 'sem_atendimento' : progress >= 70 ? 'avancada' : 'em_andamento';

  return {
    ...meta,
    habilidades: skillIds(meta),
    current,
    target,
    baseline,
    progress,
    relatedLogs: relatedLogs.length,
    lastDate: lastDate?.toISOString().slice(0, 10) || null,
    daysWithoutService,
    daysToDeadline,
    overdue,
    withoutService,
    completed,
    status,
    recommendation: completed
      ? 'Revisar a meta e definir manutenção ou novo desafio.'
      : overdue
        ? 'Replanejar prazo, estratégia e frequência de atendimento.'
        : withoutService
          ? 'Vincular a próxima sessão a esta meta.'
          : progress < 40
            ? 'Manter apoio intensivo e revisar a estratégia aplicada.'
            : progress < 70
              ? 'Manter frequência e variar atividades equivalentes.'
              : 'Consolidar desempenho em diferentes contextos.',
  };
}

export function buildPeiDashboard(pei, logs = [], today = new Date()) {
  const goals = (pei?.metas || []).map((meta) => analyzePeiGoal(meta, logs, today));
  const averageProgress = goals.length ? Math.round(goals.reduce((sum, goal) => sum + goal.progress, 0) / goals.length) : 0;
  return {
    goals,
    averageProgress,
    completed: goals.filter((goal) => goal.completed).length,
    overdue: goals.filter((goal) => goal.overdue).length,
    withoutService: goals.filter((goal) => goal.withoutService && !goal.completed).length,
    active: goals.filter((goal) => !goal.completed).length,
  };
}
